import { addDays, addMonths, format, getYear, parseISO, startOfMonth } from 'date-fns'
import { DollarSign } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { useCreateBenchmark } from 'components/hooks/useCreateBenchmark'
import { useGetQuote } from 'components/hooks/useGetQuote'
import { type BenchmarkFormProps } from 'components/prospects/types'
import { FormDateFieldDS, FormNumberFieldDS, FormRadioGroupDS } from 'components/reusable/form/FormFields'
import { Banner, RadioButton, RadioGroup } from 'slab'
import { usePatchQuotingProspectsProspectIdQuotesId } from 'utils/web_api'
import { useWindowLocation } from 'utils/windowLocation'

import QuoteFlowButtonNav from '../QuoteFlowButtonNav'
import QuoteFlowHeader from '../QuoteFlowHeader'
import QuoteInfoSection from '../QuoteInfoSection'
import { resolveNextStepPath } from '../utils'

type Props = {
  prospectId: string;
  quoteId: string;
  nextStepPath: string;
  previousStepPath: string;
  prospectName?: string;
  maxCoverageStartDate?: Date;
}

const getYearFromStartDate = (startDate?: string | null) => {
  if (!startDate) {
    return
  }

  return getYear(parseISO(startDate))
}

export function CurrentBenefits ({ prospectId, quoteId, nextStepPath, previousStepPath, prospectName, maxCoverageStartDate }: Props) {
  const { assign } = useWindowLocation()

  const [offersBenefits, setOffersBenefits] = useState<boolean>(true)
  const [error, setError] = useState<string>()
  const createBenchmarkMutation = useCreateBenchmark({ prospectId, quoteId })
  const updateQuoteMutation = usePatchQuotingProspectsProspectIdQuotesId()
  const { data: quote } = useGetQuote({ prospectId, quoteId })

  const isSubmitting = createBenchmarkMutation.isPending || updateQuoteMutation.isPending

  const firstOfNextMonth = format(startOfMonth(addMonths(new Date(), 1)), 'yyyy-MM-dd')
  const maxStartDate = maxCoverageStartDate ? format(addDays(maxCoverageStartDate, 1), 'yyyy-MM-dd') : undefined

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<BenchmarkFormProps>({
    defaultValues: {
      coverage_start_date: firstOfNextMonth,
    },
  })

  useEffect(() => {
    if (!quote) {
      return
    }

    reset({
      target_metal_tier: quote?.target_metal_tier,
      target_network_type: quote?.target_network_type,
      target_individual_deductible: quote?.target_individual_deductible,
      target_individual_oop_max: quote?.target_individual_oop_max,
      coverage_start_date: quote?.coverage_start_date || firstOfNextMonth,
    })
  }, [quote])

  const navigateToNextStep = () => {
    const path = resolveNextStepPath(nextStepPath, prospectId, quoteId)
    assign(path)
  }

  const onSubmit = (data: BenchmarkFormProps) => {
    // Get the year from the coverage start date
    data.year = getYearFromStartDate(data.coverage_start_date)

    if (offersBenefits) {
      // If they have current benefits, create benchmark (which also saves the date)
      createBenchmarkMutation.mutate(data, {
        onSuccess: () => {
          navigateToNextStep()
        },
        onError: (error) => {
          setError(`${error.message} - ${error.errors?.join(', ') || ''}`)
        },
      })
    } else {
      // If no current benefits, just save the coverage start date and navigate
      updateQuoteMutation.mutate({
        prospectId,
        id: quoteId,
        data: {
          coverage_start_date: data.coverage_start_date || undefined,
        },
      }, {
        onSuccess: () => {
          navigateToNextStep()
        },
        onError: (error: any) => {
          setError(`Failed to save coverage start date: ${error?.message || 'Unknown error'}`)
        },
      })
    }
  }

  return (
    <div className='flex justify-center'>
      <div className='flex flex-col max-w-2xl w-full'>
        <QuoteFlowHeader
          title='Current benefits structure'
          description='Tell us about any current health insurance benefits'
        />
        <div className='flex flex-col gap-6'>
          <div className='flex flex-row gap-4 flex-wrap'>
            <div className='flex-1 min-w-56'>
              <FormDateFieldDS<BenchmarkFormProps>
                error={errors.coverage_start_date}
                fieldId='coverage_start_date'
                fieldLabel='Desired coverage start date'
                register={register}
                min={firstOfNextMonth}
                max={maxStartDate}
                validationRule={{
                  required: 'Coverage start date is required.',
                  validate: (value: string) => {
                    if (new Date(value) < new Date()) {
                      return 'Coverage start date must be in the future.'
                    }
                    return true
                  },
                }}
                disabled={isSubmitting}
              />
            </div>
            <div className='flex-1 min-w-56' />
          </div>
          <RadioGroup
            name='current-benefits-structure'
            label={`Does ${prospectName || 'the company'} currently offer health insurance?`}
            disabled={isSubmitting}
          >
            <RadioButton
              value='yes'
              label='Yes'
              checked={offersBenefits}
              onChange={() => setOffersBenefits(true)}
            />
            <RadioButton
              value='no'
              label='No'
              checked={!offersBenefits}
              onChange={() => setOffersBenefits(false)}
            />
          </RadioGroup>
          {offersBenefits && (
            <QuoteInfoSection
              header='Tell us a bit about their current benefits.'
              subheader='If they offer multiple plans, choose one that you&apos;d like to benchmark around.'
            >
              <form>
                <div className='flex flex-col gap-6'>
                  <FormRadioGroupDS
                    control={control}
                    name='target_network_type'
                    label='Network type'
                    layout='horizontal'
                    buttonVariant='bordered'
                    buttonSize='small'
                    options={[
                      { label: 'PPO', value: 'ppo' },
                      { label: 'HMO', value: 'hmo' },
                      { label: 'POS', value: 'pos' },
                      { label: 'EPO', value: 'epo' },
                    ]}
                    disabled={isSubmitting}
                  />
                  <FormRadioGroupDS
                    control={control}
                    name='target_metal_tier'
                    label='Metal tier'
                    layout='horizontal'
                    buttonVariant='bordered'
                    buttonSize='small'
                    options={[
                      { label: 'Bronze', value: 'bronze' },
                      { label: 'Silver', value: 'silver' },
                      { label: 'Gold', value: 'gold' },
                      { label: 'Platinum', value: 'platinum' },
                    ]}
                    disabled={isSubmitting}
                  />
                  <div className='flex flex-row gap-4 flex-wrap'>
                    <div className='flex-1 min-w-56'>
                      <FormNumberFieldDS<BenchmarkFormProps>
                        fieldId='target_individual_deductible'
                        fieldLabel='Deductible'
                        register={register}
                        placeholder='0.00'
                        step='0.01'
                        validationRule={{
                          required: 'Deductible is required.',
                          min: {
                            value: 0,
                            message: 'Deductible cannot be negative',
                          },
                        }}
                        leadingIcon={DollarSign}
                        error={errors.target_individual_deductible}
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className='flex-1 min-w-56'>
                      <FormNumberFieldDS<BenchmarkFormProps>
                        fieldId='target_individual_oop_max'
                        fieldLabel='Out-of-pocket max'
                        register={register}
                        placeholder='0.00'
                        step='0.01'
                        validationRule={{
                          required: 'Out-of-pocket max is required.',
                          min: {
                            value: 0,
                            message: 'Out-of-pocket max cannot be negative',
                          },
                        }}
                        leadingIcon={DollarSign}
                        error={errors.target_individual_oop_max}
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                </div>
              </form>
            </QuoteInfoSection>
          )}
          {error && (
            <Banner
              title='Error creating benchmark'
              type='critical'
              onDismiss={() => setError(undefined)}
            >
              {error}
            </Banner>
          )}
          <QuoteFlowButtonNav
            onNext={() => {
              // Always submit the form to save the coverage start date
              handleSubmit(onSubmit)()
            }}
            isLoading={isSubmitting}
            onBack={() => {
              assign(previousStepPath)
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default (props: Props) => {
  return (
    <CurrentBenefits {...props} />
  )
}
